import { apiRequest, useDevelopmentFixtures } from "../api/client";
import { crmApi } from "./crm";
import type { CaseRef, WorkspaceService } from "./workspace";
export type Relationship = {
  kind: "person" | "school" | "company";
  organization: string;
  contacts: Array<{ name: string; role: string; email: string; phone: string }>;
  preferences: string;
  tags: string[];
};
export type Preparation = {
  checks: Record<string, boolean>;
  logistics: string;
  requirements: string;
  outcome: "pending" | "completed" | "not_performed";
  closingNotes: string;
  postVisitDate: string | null;
  postVisitNote: string;
  postVisitStatus: "pending" | "completed";
};
export type BusinessRecord<T> = {
  resourceId: string;
  version: number;
  data: T;
  updatedAt?: string | null;
  updatedBy?: string | null;
};
export type Write<T> = { commandId: string; expectedVersion: number; data: T };
export type AttentionItem = {
  id: string;
  kind: CaseRef["kind"];
  service: WorkspaceService;
  customer: string;
  category: string;
  detail: string;
  due_at: string | null;
  amount: number | null;
  currency: string;
};
export type AttentionPage = {
  data: AttentionItem[];
  pagination: { page: number; perPage: number; total: number };
  counts: Array<{ category: string; count: number }>;
};
export type PipelineCount = {
  stage: string;
  currency: string;
  count: number;
  amount: number | null;
  valued: number | null;
};
export type BusinessReports = {
  channels: Array<{
    source_channel: string;
    service_type: WorkspaceService;
    won: number;
    lost: number;
  }>;
  lostReasons: Array<{ lost_reason: string; count: number }>;
  postvisit: Array<{ status: string; count: number }>;
};
const root = "/api/v1/crm/business";
const read = async <T>(path: string) =>
  (await apiRequest<{ data: T }>(root + path)).data;
const put = async <T>(path: string, input: Write<T>) =>
  (
    await apiRequest<{ data: BusinessRecord<T> }>(root + path, {
      method: "PUT",
      body: JSON.stringify(input),
    })
  ).data;
export const businessApi = {
  profile: (id: string) =>
    read<BusinessRecord<Relationship>>(`/profiles/${encodeURIComponent(id)}`),
  saveProfile: (id: string, input: Write<Relationship>) =>
    put(`/profiles/${encodeURIComponent(id)}`, input),
  preparation: (ref: CaseRef) =>
    read<BusinessRecord<Preparation>>(
      `/preparation/${ref.kind}/${encodeURIComponent(ref.id)}`,
    ),
  savePreparation: (ref: CaseRef, input: Write<Preparation>) =>
    put(`/preparation/${ref.kind}/${encodeURIComponent(ref.id)}`, input),
  attention: async (query: Record<string, string>): Promise<AttentionPage> => {
    if (useDevelopmentFixtures) {
      const today = await crmApi.today();
      const all = today.priorities
        .filter((p) => !query.service || p.service === query.service)
        .map((p) => ({
          id: p.opportunityId,
          kind: "opportunity" as const,
          service: p.service,
          customer: p.customerName,
          category: "followup",
          detail: p.note,
          due_at: p.dueAt,
          amount: null,
          currency: "MXN",
        }));
      const filtered = all.filter(
        (p) => !query.category || p.category === query.category,
      );
      const page = Number(query.page) || 1,
        perPage = Number(query.per_page) || 8;
      return {
        data: filtered.slice((page - 1) * perPage, page * perPage),
        pagination: { page, perPage, total: filtered.length },
        counts: [{ category: "followup", count: all.length }],
      };
    }
    return apiRequest<AttentionPage>(
      root + "/attention?" + new URLSearchParams(query),
    );
  },
  pipeline: async (service: string, search = ""): Promise<PipelineCount[]> => {
    if (useDevelopmentFixtures) {
      const list = await crmApi.listOpportunities(1, {
        service: service as WorkspaceService,
        search,
      });
      return ["new", "conversation", "quote", "awaiting", "won", "lost"].map(
        (stage) => ({
          stage,
          currency: "MXN",
          count: list.data.filter((r) => r.stage === stage).length,
          amount: null,
          valued: null,
        }),
      );
    }
    return read<PipelineCount[]>(
      "/pipeline?" +
        new URLSearchParams({ service, ...(search ? { search } : {}) }),
    );
  },
  reports: (from: string, to: string, service: string) =>
    read<BusinessReports>(
      "/reports?" +
        new URLSearchParams({ from, to, ...(service ? { service } : {}) }),
    ),
};
